import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react'
import Text from '../components/atoms/Text/Text'
import logo from '../assets/images/logo.svg'
import { setApiTimeoutCallback } from '../libs/api'
import { error as logError } from '../utils/logger'

export const idTimeoutModal = 'modal-network-error-timeout'
export const idTimeoutModalTitle = `${idTimeoutModal}-title`
export const idTimeoutModalMessage1 = `${idTimeoutModal}-message-1`
export const idTimeoutModalMessage2 = `${idTimeoutModal}-message-2`

interface NetworkErrorHandlerContextValue {
  isEnabled: boolean

  hasTimeoutError: boolean
  setHasTimeoutError: React.Dispatch<React.SetStateAction<boolean>>
}

const initialContextValue: NetworkErrorHandlerContextValue = {
  isEnabled: false,

  hasTimeoutError: false,
  setHasTimeoutError: () => {}
}

export const NetworkErrorHandlerContext = createContext<NetworkErrorHandlerContextValue>(initialContextValue)

export const NetworkErrorHandlerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isEnabled, setIsEnabled] = useState<boolean>(false)
  const [hasTimeoutError, setHasTimeoutError] = useState<boolean>(false)

  const timeoutCallback = () => {
    logError('Repeated network timeouts detected.')
    setHasTimeoutError(true)
  }

  useEffect(() => {
    setApiTimeoutCallback(timeoutCallback)
    setIsEnabled(true)
  }, [])

  const timeoutModal = useMemo((): JSX.Element => {
    return (
      <div
        id={idTimeoutModal}
        data-testid={idTimeoutModal}
        className='bg-black flex justify-center items-center min-h-screen w-full p-4'
        style={{
          backgroundImage: 'radial-gradient(circle at top, #2C2C2C, #090909)'
        }}
      >
        <header className='absolute left-[40px] top-[40px]' aria-hidden='true'>
          <img src={logo} className='w-[138px]' alt='logo' />
        </header>
        <div className='glow-effect max-w-md w-full'>
          <div className='diamond-corner p-1 bg-dopage-lightGreen w-full'>
            <div className='inner bg-black'>
              <p
                id={idTimeoutModalTitle}
                data-testid={idTimeoutModalTitle}
                className='absolute p-2 font-bold mb-6 w-full text-center text-dopage-lightGreen'
              >
                <Text
                  className='text-center w-full'
                  bold
                  size='SM'
                  text='Trying to connect but...'
                />
              </p>

              <div className='py-8 px-4 flex flex-col justify-center items-center h-[384px]'>
                <p
                  id={idTimeoutModalMessage1}
                  data-testid={idTimeoutModalMessage1}
                  className='font-bold mb-6 w-[60%] text-center text-dopage-lightGreen'
                >
                  <Text
                    className='text-center w-full'
                    bold
                    size='BASE'
                    text='Your Internet is ghosting us :('
                  />
                </p>
                <p
                  id={idTimeoutModalMessage2}
                  data-testid={idTimeoutModalMessage2}
                  className='text-center mb-4 w-[60%] text-dopage-darkGreen'
                >
                  <Text
                    className='w-full'
                    size='SM'
                    text='Please check your Internet connection and try again.'
                  />
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }, [])

  return (
    <NetworkErrorHandlerContext.Provider
      value={{
        isEnabled,

        hasTimeoutError,
        setHasTimeoutError
      }}
    >
      <div>
        {isEnabled && hasTimeoutError && timeoutModal}
        {children}
      </div>
    </NetworkErrorHandlerContext.Provider>
  )
}

export const useNetworkErrorHandler = (): NetworkErrorHandlerContextValue => {
  return useContext(NetworkErrorHandlerContext)
}
